import get from 'lodash/get';
import { PRIMARY_APPLICANT_TYPE, CO_APPLICANT_TYPE } from '~/containers/App/constants';
import isRedirectedFromAuto from '~/routes/util/isRedirectedFromAuto';
import { QueryResponse, FormatStoreToProps } from '../types';

const currentProperty = (application: ApplicationQuery) => {
  const properties = application.properties || [];

  return properties[0];
};

const primaryHomeApplicant = (application: ApplicationQuery) => {
  const homeApplicants = application.homeApplicants || [];

  return homeApplicants.find((applicant) => applicant.applicantType === PRIMARY_APPLICANT_TYPE);
};

const primaryAutoApplicant = (application: ApplicationQuery) => {
  const autoApplicants = application.autoApplicants || [];

  return autoApplicants.find((applicant) => applicant.applicantType === PRIMARY_APPLICANT_TYPE);
};

const homeCoApplicant = (application: ApplicationQuery) => {
  const homeApplicants = application.homeApplicants || [];

  return homeApplicants.find((applicant) => applicant.applicantType === CO_APPLICANT_TYPE);
};

const autoCoApplicant = (application: ApplicationQuery) => {
  const autoApplicants = application.autoApplicants || [];

  return autoApplicants.find((applicant) => applicant.applicantType === CO_APPLICANT_TYPE);
};

const currentUserProfile = (
  application: ApplicationQuery,
  flow: string,
  redirectedFromAuto: boolean
) => {
  if (application.isAutoOnly || flow === 'auto' || redirectedFromAuto) {
    return primaryAutoApplicant(application).userProfile;
  }

  return primaryHomeApplicant(application).userProfile;
};

const isNewPurchaseNonBundler = (application: ApplicationQuery) =>
  !application.bundled && get(currentProperty(application), 'newPurchase', null);

const isReshopperNonBundler = (application: ApplicationQuery) =>
  !application.bundled && !get(currentProperty(application), 'newPurchase', null);

const isReturningCustomer = (application: ApplicationQuery) =>
  application.inForcePolicies.length > 0;

const hasNoQuotes = (application: ApplicationQuery) => {
  const { automateQuoteEligibility, completeInstantQuoteEstimates } = application;

  const hasQuotesToDiscuss = completeInstantQuoteEstimates?.length > 0;

  return (
    automateQuoteEligibility === 'ineligible' ||
    sessionStorage.getItem('eligibleNoQuotes') === 'true' ||
    !hasQuotesToDiscuss
  );
};

const formatStoreToProps = (
  { application }: QueryResponse,
  additionalProps: any = {}
): FormatStoreToProps => ({
  ...additionalProps,
  application,
  currentProperty: currentProperty(application),
  primaryHomeApplicant: primaryHomeApplicant(application),
  primaryAutoApplicant: primaryAutoApplicant(application),
  homeCoApplicant: homeCoApplicant(application),
  autoCoApplicant: autoCoApplicant(application),
  currentUserProfile: currentUserProfile(
    application,
    additionalProps.flow,
    isRedirectedFromAuto(additionalProps?.history?.location)
  ),
  hasAutoApplicants: application.autoApplicants.length > 0,
  hasHomeApplicants: application.homeApplicants.length > 0,
  isNewPurchaseNonBundler: isNewPurchaseNonBundler(application),
  isReshopperNonBundler: isReshopperNonBundler(application),
  isReturningCustomer: isReturningCustomer(application),
  hasNoQuotes: hasNoQuotes(application),
  isRedirectedFromAuto: isRedirectedFromAuto(additionalProps?.history?.location),
});

export default formatStoreToProps;
